// @flow
import React from "react";
import Media from "react-media";
import styled, { ThemeProvider } from "styled-components";
import Confetti from "react-confetti";
import Measure from "../../../common/Measure";
import Gettext from "../../../common/Gettext";
import {
  ItemRate,
  ItemBalance,
  ItemResult
} from "../../../Tournament/PersonalInfo";
import DarkRateTable from "../../../Tournament/DarkRateTable";
import { FinalCup as RawFinalCup } from "../../../Icons/Tournaments";
import RawLink from "../../Link";
import TournamentAPI from "../../Api";
import { TradeThemeContext } from "../../../ThemeProvider";

import Header from "./Header";

import * as S from "./styles";

const FinalCup = styled(RawFinalCup)`
  display: block;
  flex-shrink: 0;

  @media (min-width: 960px), (max-width: 479px) {
    margin: 0 auto 25px;
  }

  @media (min-width: 480px) and (max-width: 959px) {
    align-self: center;
    flex-basis: 123px;
  }
`;

const Text = styled.div`
  width: 100%;
  padding: 0 16px;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  color: ${({ theme }) => theme.foreground};
`;

const Heading = styled(Text)`
  margin-bottom: 6px;
  color: ${({ theme }) => theme.accent};
`;

const Title = styled(Text)`
  margin-bottom: 9px;

  @media (max-width: 959px) {
    line-height: 2;
  }
`;

const InfoBlock = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-grow: 0;
  flex-shrink: 0;
  width: 100%;
  padding: 14px 32px;
  margin-bottom: 27px;
  background-color: ${({ theme }) => theme.mainDark};

  @media (max-width: 479px) {
    padding: 14px 20px;
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const InfoTitle = styled.div`
  align-self: center;
  width: 100%;
  max-width: 100px;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  line-height: 23px;
  font-weight: 700;
  color: ${({ theme }) => theme.foreground};

  @media (min-width: 960px) {
    padding-right: 20px;
  }

  @media (max-width: 479px) {
    max-width: none;
    margin-bottom: 10px;
  }
`;

const InfoBlockItems = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;

  @media (max-width: 959px) {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const Container = styled.div`
  @media (max-width: 959px) {
    width: 100%;
  }
`;

const TableContainer = styled(Container)`
  padding: 0 20px;

  @media (max-width: 959px) {
    flex-grow: 1;
    flex-shrink: 0;
  }
`;

const LinkContainer = styled(Container)`
  flex-shrink: 0;
  align-self: flex-end;
`;

const Link = styled(RawLink)`
  margin-top: 0;
`;

type Props = {
  in: boolean,
  onDismiss: () => void,
  api: any,
  id: number,
  prize: number,
  prizeUSD: number
};

class FinalPopup extends React.Component<Props, {}> {
  state = {
    limit: 5
  };

  componentDidMount() {
    const tournamentInfo = async () => {
      const { api, id: tournamentId } = this.props;
      if (tournamentId != null) {
        const info = await api.fetchEntity(
          `/api/tournaments/info?id=${tournamentId}`
        );

        if (info != null) {
          this.setState(
            {
              limit:
                (info.value && info.value.prizes && info.value.prizes.length) ||
                5
            },
            () =>
              api.fetchEntity(
                `/api/tournaments/leaderboard?tournament_id=${tournamentId}&limit=${
                  this.state.limit
                }`
              )
          );
        }
      }
    };

    tournamentInfo();
  }

  render() {
    const { in: inProp, onDismiss, style, api, ...props } = this.props;
    const { id: tournamentId } = props;

    if (tournamentId == null) {
      return null;
    }

    const leaderboard = api.readEntity(
      `/api/tournaments/leaderboard?tournament_id=${tournamentId}&limit=${
        this.state.limit
      }`
    );
    const info = api.readEntity(`/api/tournaments/info?id=${tournamentId}`);

    if (info.value == null) {
      return null;
    }

    const tournament = info.value;

    return (
      <S.Popup in={inProp} style={style} onDismiss={onDismiss} fades={false}>
        <S.Wrapper>
          <Header
            Cup={() => (
              <Media query="(max-width: 479px)">
                {matches => <FinalCup width={matches ? "61px" : "123px"} />}
              </Media>
            )}
            {...tournament}
          >
            <Heading>
              <Gettext>The tournament is finished</Gettext>
            </Heading>
            <Title>{tournament.title}</Title>
          </Header>
          <InfoBlock>
            <InfoTitle>
              <Gettext>Your results</Gettext>:
            </InfoTitle>
            <InfoBlockItems>
              <ItemBalance
                horizontal
                balance={tournament.account && tournament.account.balance}
                participation_status={tournament.participation_status}
                account={tournament.account}
              />
              <ItemRate
                horizontal
                current_place={tournament.current_place}
                participant_count={tournament.participant_count}
              />
              <ItemResult
                horizontal
                value={props.prize}
                valueUSD={props.prizeUSD}
              />
            </InfoBlockItems>
          </InfoBlock>
          {leaderboard.value != null && (
            <TableContainer>
              <DarkRateTable leaderboard={leaderboard.value} />
            </TableContainer>
          )}
          <LinkContainer>
            <Link to={`/tournaments/${props.id}`}>
              <Gettext>Learn more</Gettext>
            </Link>
          </LinkContainer>
        </S.Wrapper>
        {tournament.current_place === 1 && (
          <Measure>
            {({ width, height, innerRef, didMeasure }) => (
              <div
                ref={innerRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  pointerEvents: "none"
                }}
              >
                {didMeasure && (
                  <Confetti
                    width={width}
                    height={height}
                    numberOfPieces={100}
                    recycle={false}
                    gravity={0.5}
                  />
                )}
              </div>
            )}
          </Measure>
        )}
      </S.Popup>
    );
  }
}

export default props => {
  const api = React.useContext(TournamentAPI);
  const theme = React.useContext(TradeThemeContext);

  return (
    <ThemeProvider theme={theme}>
      <FinalPopup {...props} api={api} key={props.id} />
    </ThemeProvider>
  );
};



// WEBPACK FOOTER //
// ./src/components/Tournaments/Popups/Final/Final.js